import { Form } from '@/components/forms';
import Input from '@/components/forms/Input';
import MultiSelect from '@/components/forms/MultiSelect';
import Select from '@/components/forms/Select';
import { Button } from '@/design-system/button';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { Link } from '@tanstack/react-router';
import { Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { SNS } from '../../Sns';
import { closingDateOptions } from '../../store-management.constant';
import { StoreInfo } from '../../StoreInfo';
import useAddStoreInfo from '../hooks/useAddStoreInfo';
import { useUpdateStoreFunction } from '../utils/updateStoreFunctions';

export const AddStoreForm = () => {
  const { mutateAsync } = useAddStoreInfo();
  const { methods, options, lastEntryOptions, prefectureOptions, holidaysOptions, otherHolidaysOptions } =
    useUpdateStoreFunction();
  const { t } = useDynamicLocale();
  return (
    <Form
      form={methods}
      onSubmit={async () => {
        const formData = methods.getValues();
        const profileImageFile = formData.profileImage?.valueOf();
        const coverImageFile = formData.coverImage?.valueOf();
        if (!profileImageFile) {
          toast.error(t('common.form.validation.profileUpload'));
          return;
        }
        if (!coverImageFile) {
          toast.error(t('common.form.validation.coverUpload'));
          return;
        }
        const profileImageUrl = await uploadFileToS3WithPresignedUrl(profileImageFile as FileObject);
        const coverImageUrl = await uploadFileToS3WithPresignedUrl(coverImageFile as FileObject);
        const payload = {
          profileImage: profileImageUrl || '',
          coverImage: coverImageUrl || '',
          storeName: formData.storeName || '',
          storeNameFurigana: formData.storeNameFurigana || '',
          groupName: formData.groupName || '',
          landlineNumber: formData.landlineNumber || '',
          description: formData.description || '',
          postalCode: formData.postalCode || '',
          prefecture: formData.prefecture || '',
          city: formData.city || '',
          street: formData.street || '',
          buildingNo: formData.buildingNo || '',
          workingTimeFrom: formData.workingTimeFrom || '',
          workingTimeTo: formData.workingTimeTo || '',
          lastEntryTime: formData.lastEntryTime || '',
          lastOrderTime: formData.lastOrderTime || '',
          holidays: formData.holidays || '',
          tax: formData.tax || '',
          closingDate: formData.closingDate || '',
          otherHolidays: formData.otherHolidays ?? [],
          instagram: formData.instagram || '',
          tiktok: formData.tiktok || '',
          line: formData.line || '',
          twitter: formData.twitter || '',
          website: formData.website || '',
          googleMap: formData.googleMap || '',
        };
        await mutateAsync(payload);
      }}
      className="relative"
    >
      <div className="relative">
        <StoreInfo />
        <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20 mt-4">
          <div className="w-[200px] xl:w-[245px] shrink-0">
            <p className="text-base font-semibold pl-12">
              <Translation id="storeManagement.basicStores.manageStoreInfo.clubAddress" />
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <Controller
              name="postalCode"
              control={methods.control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label="storeManagement.basicStores.manageStoreInfo.clubAddress.postalCode"
                  placeholder="100-0101"
                  inputMode="numeric"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    let value = e.target.value;

                    // keep digits only
                    const digits = value.replace(/\D/g, '');

                    // max 7 digits
                    if (digits.length > 7) return;

                    // format as XXX-XXXX
                    if (digits.length <= 3) {
                      value = digits;
                    } else {
                      value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
                    }

                    field.onChange(value);
                  }}
                />
              )}
            />

            <Select
              options={prefectureOptions}
              placeholder={t('common.form.placeholder.select')}
              label="storeManagement.basicStores.manageStoreInfo.clubAddress.prefecture"
              {...methods.register('prefecture')}
            ></Select>

            <Input
              label="storeManagement.basicStores.manageStoreInfo.clubAddress.city"
              placeholder={t('common.form.placeholder.city')}
              maxLength={50}
              {...methods.register('city')}
            />
            <Input
              label="storeManagement.basicStores.manageStoreInfo.clubAddress.street"
              placeholder={t('common.form.placeholder.street')}
              maxLength={50}
              {...methods.register('street')}
            />
            <Input
              label="storeManagement.basicStores.manageStoreInfo.clubAddress.buildingAndRoomNo"
              placeholder={t('common.form.placeholder.buildingAndRoom')}
              maxLength={50}
              {...methods.register('buildingNo')}
            />
          </div>
        </div>

        <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20 mt-4">
          <div className="w-[200px] xl:w-[245px] shrink-0">
            <p className="text-base font-semibold pl-12">
              <Translation id="storeManagement.basicStores.manageStoreInfo.clubInfo" />
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <Select
              options={options}
              placeholder={t('storeManagement.storeInfo.timePlaceholder')}
              label="storeManagement.basicStores.manageStoreInfo.clubInfo.workingHoursFrom"
              {...methods.register('workingTimeFrom')}
            />
            <Select
              options={options}
              placeholder={t('storeManagement.storeInfo.timePlaceholder')}
              label="storeManagement.basicStores.manageStoreInfo.clubInfo.workingHoursTo"
              {...methods.register('workingTimeTo')}
            />
            <Select
              options={lastEntryOptions}
              placeholder={t('storeManagement.storeInfo.timePlaceholder')}
              label="storeManagement.basicStores.manageStoreInfo.clubInfo.lastEntryTime"
              {...methods.register('lastEntryTime')}
            />
            <Select
              options={holidaysOptions}
              placeholder={t('common.form.placeholder.select')}
              label="storeManagement.basicStores.manageStoreInfo.clubInfo.holidays"
              {...methods.register('holidays')}
            ></Select>
            <Select
              options={closingDateOptions}
              placeholder={t('common.form.placeholder.select')}
              label="storeManagement.basicStores.manageStoreInfo.clubInfo.closingDate"
              {...methods.register('closingDate')}
            ></Select>

            <MultiSelect
              name={'otherHolidays'}
              label="storeManagement.basicStores.manageStoreInfo.clubInfo.otherHolidays"
              options={otherHolidaysOptions}
              className="flex"
              placeholder={t('common.form.placeholder.select')}
              emptyIndicator={<p className="text-center text-sm">No Options</p>}
            />
          </div>
        </div>
        <SNS />
      </div>

      <div className="flex flex-row-reverse w-full gap-4 text-sm mt-6">
        <Button type="submit" className="w-[110px]">
          <Translation id="common.page.button.save" />
        </Button>
        <Link to="/store-information/basic-stores">
          <Button variant="outline" className="w-[122px] bg-transparent">
            <Translation id="common.page.button.cancel" />
          </Button>
        </Link>
      </div>
    </Form>
  );
};
